/**
 * fivethings.mjs — the whole catalogue, as a loop that spends most of its life waiting.
 *
 * FOUNDER, 2026-08-14: "ch7 next" … then "ch1 too".
 *
 * ── I SAID THIS CHAPTER MIGHT NOT WANT AN INSTRUMENT. IT DOES, AND HERE IS WHY ────────────────
 * Chapter 1 is definitional, and I said so: its lesson is what software IS, not how something
 * behaves. But §4 contains a claim that prose can only assert and a picture can demonstrate —
 * "Wait for next input — sit ready for the next tap… Most software spends most of its time in this
 * state." That is a statement about PROPORTION. A learner reads it and nods; a learner who watches
 * the ring sit in WAIT while the other four flash past has felt it. That is the instrument, and it
 * is one sentence of his that earns it.
 *
 * ── WHAT THIS ENGINE WILL NOT DO ──────────────────────────────────────────────────────────────
 * It will not decompose an app for the learner. §4 names three examples — "The calculator does all
 * five every time you tap a number. WhatsApp does all five every time a message arrives. The bank
 * app does all five every time you check your balance" — and it decomposes none of them, because
 * §4's closing move is to hand the five QUESTIONS to the reader: "where is its input coming from?
 * Where is the work being done… What is it remembering, and where?" Filling those in on his behalf
 * would take the exercise away and replace it with my answer. The engine holds his five things, his
 * five questions, and nothing about any particular app.
 */

export const SOURCE_4 = 'Code101 module 01 §4 "The five things every piece of software does"';
export const SOURCE_6 = 'Code101 module 01 §6 "Software does not “know” anything"';
export const SOURCE_1 = 'Code101 module 01 §1 "A machine that does what it is told, fast"';

export const WHOLE_CATALOGUE = 'Strip away the surface — the icons, the colours, the buttons — and every piece of software you have ever used is doing some combination of five things. Five. That is the whole catalogue.';

/**
 * The five, in his order and his words. `dwell` is how long the ring rests on each — and it is not
 * a design choice, it is the one claim in §4 that this lab exists to show: waiting is where the
 * time goes. The other four are given equal, short dwells because he ranks none of them.
 */
export const THINGS = Object.freeze([
  { n: 1, id: 'input', name: 'Take input', dwell: 1,
    text: 'read something from outside. A tap on the screen, a keystroke, a voice command, a sensor reading, a file from the internet.' },
  { n: 2, id: 'work', name: 'Do work', dwell: 1,
    text: 'apply the recipe\'s instructions to the input. Calculate. Sort. Compare. Transform.' },
  { n: 3, id: 'remember', name: 'Remember', dwell: 1,
    text: 'write something down so it survives. Save the file. Remember the user. Log the event.' },
  { n: 4, id: 'output', name: 'Show output', dwell: 1,
    text: 'put something back into the world. A pixel on the screen. A sound. A printed page. A message sent to another machine.' },
  { n: 5, id: 'wait', name: 'Wait for next input', dwell: 12,
    text: 'sit ready for the next tap, the next keystroke, the next sensor reading. Most software spends most of its time in this state.' },
]);

/** The share of a cycle spent waiting — COMPUTED from the dwells, so it cannot drift from the ring. */
export const waitShare = (things = THINGS) => {
  const total = things.reduce((t, x) => t + x.dwell, 0);
  return Math.round((100 * (things.find((x) => x.id === 'wait')?.dwell || 0)) / total);
};

/** §4's three examples, quoted. He names them and decomposes none — and neither does this. */
export const HIS_EXAMPLES = Object.freeze([
  'The calculator does all five every time you tap a number.',
  'WhatsApp does all five every time a message arrives.',
  'The bank app does all five every time you check your balance.',
]);
export const NOT_IN_THE_FIVE = 'The complexity of any real product is not in these five — it is in how many layers of these five are stacked on top of each other to produce the experience you see.';

/**
 * §4's five questions, verbatim — the worksheet. These are what the learner answers; the lab never
 * answers them. His Personal Project task for Day 1 says the same and adds the rule that makes an
 * empty slot honest rather than a failure.
 */
export const QUESTIONS = Object.freeze([
  { id: 'input', ask: 'where is its input coming from?' },
  { id: 'work', ask: 'Where is the work being done — on my phone, or on a far-away machine?' },
  { id: 'remember', ask: 'What is it remembering, and where?' },
  { id: 'output', ask: 'What is being shown back?' },
  { id: 'wait', ask: 'What is it waiting for next?' },
]);
export const EMPTY_IS_AN_ANSWER = 'If a slot is empty, say so — that is also an answer.';
export const A_WORKING_MAP = 'These five questions, applied to any app, give you a working map of how it actually works.';

/** §6 — the translation, and the four questions that do it. */
export const DOES_NOT_KNOW = 'The single biggest mistake non-technical people make about software is to imagine it as a smart agent that understands the world. It does not.';
export const MARKETING = '"Our software knows what your customers want." It does not. It has a recipe that looks at the data and produces a guess.';
export const TRANSLATE = 'Your job, as a literate user, is to translate marketing back into recipe-language whenever a decision matters.';
export const FOUR_QUESTIONS = Object.freeze([
  'What is the recipe?', 'What data does it use?', 'What does it produce?', 'What does it not know?',
]);
export const AI_IS_NEXT = 'AI is the first kind of recipe that looks like it understands, and recognising that it does not is the entire literacy of the next course.';

/** §1's one line. */
export const RECIPE = 'software is a recipe written down so a machine can follow it, step by step, without the human in the room.';
